import { useCallback, ReactNode } from 'react'

import type { Course, Chapter } from '@mc/api'
import {
  Button,
  type ButtonKindValue,
  type ButtonSizeValue,
} from '@mc/design-system'
import { useStaticData } from '@mc/static-data-react'
import { usePrimaryCta, EnhancedCtaCopy } from '@mc/growth'

import { useQuizPrimerCta } from 'experiments/pen-5557_v1_quiz_primer/hooks'

import { trackCTAClicked } from './events'
import type { trackPropsType } from './events'

interface CTAProps {
  children?: ReactNode
  className?: string
  fullWidth?: boolean
  href?: string
  kind?: ButtonKindValue
  onClick?: (action: string, href: string) => void
  size?: ButtonSizeValue
  trackProps?: trackPropsType
  trackPurchaseClick?: boolean
  isBasic?: boolean
  style?: React.CSSProperties
}

const CTA = ({
  children,
  className,
  fullWidth = false,
  href: customHref,
  kind = 'primary',
  onClick = () => {},
  size = 'md',
  trackProps,
  trackPurchaseClick = true,
  ...props
}: CTAProps) => {
  const { ctaCopy, ctaHref } = usePrimaryCta()
  const { ctaHref: quizPrimerCtaHref } = useQuizPrimerCta()
  const resultHref = customHref || quizPrimerCtaHref || ctaHref
  const { course, episode } = useStaticData<{
    course: Course
    episode: Chapter
  }>()

  let action = ctaCopy
  if (children) {
    action = typeof children === 'string' ? children : 'cta text unknown'
  }

  const handleClick = useCallback(() => {
    if (trackPurchaseClick) {
      trackCTAClicked(
        course as Course,
        episode as Chapter,
        {
          action,
          href: resultHref,
          ...trackProps,
        } as trackPropsType,
      )
    }
    onClick(action, resultHref)
  }, [
    action,
    resultHref,
    episode,
    course,
    onClick,
    trackProps,
    trackPurchaseClick,
  ])

  return (
    <Button
      as='a'
      className={className}
      fullWidth={fullWidth}
      href={resultHref}
      kind={kind}
      onClick={handleClick}
      size={size}
      {...props}
    >
      <EnhancedCtaCopy size={size}>{children || ctaCopy}</EnhancedCtaCopy>
    </Button>
  )
}

export default CTA
